/**
 * Hook centralisé pour la gestion des statuts des chantiers
 *
 * Ce hook est le POINT UNIQUE pour tous les changements de statut.
 * Il réutilise les règles définies dans statuts.ts et émet des événements
 * pour synchroniser toutes les vues de l'application.
 *
 * Migré vers React Query (useMutation + invalidation).
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { invalidateChantier } from '@/lib/queryInvalidation';
import {
  StatutChantier,
  TransitionOption,
  isTransitionAutorisee,
  getModalForTransition,
  getTransitionsPossibles,
  canBeArchived,
  STATUTS_CONFIG,
} from '@/constants/statuts';
import { useChantierArchive } from './useChantierArchive';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface UseChantierStatusProps {
  onSuccess?: () => void;
}

interface StatusTransitionResult {
  success: boolean;
  modalRequired?: string;
  error?: string;
}

interface ChantierMinimal {
  id: string;
  etat_chantier: string;
  etat_avant_archivage?: string | null;
}

interface DateParams {
  dateDebutPrevue?: string | null;
  dateDebutReelle?: string | null;
  dateFinPrevue?: string | null;
  dateFinReelle?: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Événements globaux (conservés pour hooks non encore migrés)
// ═══════════════════════════════════════════════════════════════════════════

function emitStatusChangeEvents(chantierId: string, oldStatus: string, newStatus: string) {
  window.dispatchEvent(new CustomEvent('chantier-status-updated', {
    detail: { chantierId, oldStatus, newStatus }
  }));
  window.dispatchEvent(new CustomEvent('chantier-metrics-updated', {
    detail: { chantierId }
  }));
}

// ═══════════════════════════════════════════════════════════════════════════
// Hook principal
// ═══════════════════════════════════════════════════════════════════════════

export function useChantierStatus({ onSuccess }: UseChantierStatusProps = {}) {
  const queryClient = useQueryClient();
  const { entrepriseId } = useSubscription();

  // Utiliser le hook d'archivage existant avec callback de succès
  const archiveHook = useChantierArchive({
    onSuccess: () => {
      onSuccess?.();
    }
  });

  // -----------------------------------------------------------------------
  // Mutation : changement de statut
  // -----------------------------------------------------------------------
  const changeStatusMutation = useMutation({
    mutationFn: async ({ chantierId, toStatus }: { chantierId: string; toStatus: StatutChantier }) => {
      const { error } = await supabase
        .from('chantiers')
        .update({ etat_chantier: toStatus })
        .eq('id', chantierId);

      if (error) throw error;
    },
    onSuccess: (_, { chantierId, toStatus }) => {
      // Invalidation React Query
      if (entrepriseId) {
        invalidateChantier(queryClient, entrepriseId, chantierId);
      }
    },
  });

  // -----------------------------------------------------------------------
  // Helpers de validation (fonctions pures — inchangés)
  // -----------------------------------------------------------------------

  const getAvailableTransitions = useCallback((
    currentStatus: StatutChantier,
    dates?: DateParams
  ): TransitionOption[] => {
    return getTransitionsPossibles(
      currentStatus,
      dates?.dateDebutPrevue,
      dates?.dateDebutReelle,
      dates?.dateFinPrevue,
      dates?.dateFinReelle
    );
  }, []);

  const canTransitionTo = useCallback((
    from: StatutChantier,
    to: StatutChantier
  ): boolean => {
    return isTransitionAutorisee(from, to);
  }, []);

  const getModalRequired = useCallback((
    from: StatutChantier,
    to: StatutChantier
  ): string | undefined => {
    return getModalForTransition(from, to);
  }, []);

  // -----------------------------------------------------------------------
  // changeStatus — wrapper qui valide puis appelle la mutation
  // -----------------------------------------------------------------------

  const changeStatus = useCallback(async (
    chantierId: string,
    fromStatus: StatutChantier,
    toStatus: StatutChantier
  ): Promise<StatusTransitionResult> => {
    // Cas spécial : "reprise" n'est pas un vrai statut, c'est un signal pour ouvrir le modal
    if (toStatus === 'reprise' as any) {
      return {
        success: false,
        modalRequired: 'ModalRepriseChantier'
      };
    }

    // Vérifier si la transition est autorisée
    if (!isTransitionAutorisee(fromStatus, toStatus)) {
      const fromLabel = STATUTS_CONFIG[fromStatus]?.label || fromStatus;
      const toLabel = STATUTS_CONFIG[toStatus]?.label || toStatus;
      const errorMsg = `Transition non autorisée : "${fromLabel}" → "${toLabel}"`;
      toast.error(errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    }

    // Vérifier si un modal est requis
    const modalRequired = getModalForTransition(fromStatus, toStatus);
    if (modalRequired) {
      return {
        success: false,
        modalRequired
      };
    }

    // Effectuer la transition via la mutation React Query
    try {
      await changeStatusMutation.mutateAsync({ chantierId, toStatus });

      // Émettre les événements de synchronisation (pour hooks non migrés)
      emitStatusChangeEvents(chantierId, fromStatus, toStatus);

      const toLabel = STATUTS_CONFIG[toStatus]?.label || toStatus;
      toast.success(`Statut modifié : "${toLabel}"`);

      onSuccess?.();
      return { success: true };

    } catch (error: any) {
      console.error('[useChantierStatus] Erreur changement statut:', error);
      toast.error(error.message || 'Erreur lors du changement de statut');
      return {
        success: false,
        error: error.message || 'Erreur inconnue'
      };
    }
  }, [onSuccess, changeStatusMutation, entrepriseId]);

  // -----------------------------------------------------------------------
  // Archive / Désarchive
  // -----------------------------------------------------------------------

  const archive = useCallback(async (chantier: ChantierMinimal): Promise<boolean> => {
    const result = await archiveHook.archiver(chantier);
    if (result) {
      emitStatusChangeEvents(chantier.id, chantier.etat_chantier, 'archive');
      if (entrepriseId) {
        invalidateChantier(queryClient, entrepriseId, chantier.id);
      }
    }
    return result;
  }, [archiveHook, entrepriseId, queryClient]);

  const unarchive = useCallback(async (chantier: ChantierMinimal): Promise<boolean> => {
    const previousStatus = chantier.etat_avant_archivage || 'termine';
    const result = await archiveHook.desarchiver(chantier);
    if (result) {
      emitStatusChangeEvents(chantier.id, 'archive', previousStatus);
      if (entrepriseId) {
        invalidateChantier(queryClient, entrepriseId, chantier.id);
      }
    }
    return result;
  }, [archiveHook, entrepriseId, queryClient]);

  const canArchive = useCallback((etatChantier: StatutChantier): boolean => {
    return canBeArchived(etatChantier);
  }, []);

  return {
    // State
    loading: changeStatusMutation.isPending || archiveHook.loading,

    // Helpers de validation
    getAvailableTransitions,
    canTransitionTo,
    getModalRequired,
    canArchive,

    // Actions
    changeStatus,
    archive,
    unarchive,
  };
}

export type { StatusTransitionResult, ChantierMinimal, DateParams };
